import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Archive, Loader2, Plus, Search, Lock } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import StaffAvatar from "@/components/StaffAvatar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export const CASE_STATUS_LABELS = {
  referral_received: "Referral Received",
  awaiting_information: "Awaiting Information",
  ready_for_initial_assessment: "Ready for Initial Assessment",
  initial_assessment_scheduled: "Initial Assessment Scheduled",
  assessment_completed: "Assessment Completed",
  suitable_for_training: "Suitable for Training",
  not_currently_suitable: "Not Currently Suitable",
  further_assessment_required: "Further Assessment Required",
  awaiting_trainer: "Awaiting Trainer",
  training_active: "Training Active",
  training_paused: "Training Paused",
  final_assessment_due: "Final Assessment Due",
  completed: "Completed",
  cancelled: "Cancelled",
  archived: "Archived",
};

const STATUS_STYLES = {
  referral_received: "bg-sky-100 text-sky-800 border-transparent",
  awaiting_information: "bg-amber-100 text-amber-800 border-transparent",
  ready_for_initial_assessment: "bg-indigo-100 text-indigo-800 border-transparent",
  initial_assessment_scheduled: "bg-indigo-100 text-indigo-800 border-transparent",
  assessment_completed: "bg-slate-200 text-slate-700 border-transparent",
  suitable_for_training: "bg-ukgreen-light text-ukgreen border-transparent",
  not_currently_suitable: "bg-slate-100 text-slate-600 border-transparent",
  awaiting_trainer: "bg-amber-100 text-amber-800 border-transparent",
  training_active: "bg-ukgreen-light text-ukgreen border-transparent",
  training_paused: "bg-amber-100 text-amber-800 border-transparent",
  final_assessment_due: "bg-orange-100 text-orange-800 border-transparent",
  completed: "bg-emerald-100 text-emerald-800 border-transparent",
  cancelled: "bg-slate-100 text-slate-500 border-transparent",
  archived: "bg-slate-100 text-slate-500 border-transparent",
};

export function CaseStatusBadge({ status, testid }) {
  return (
    <Badge data-testid={testid} className={`${STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-transparent"} text-xs`}>
      {CASE_STATUS_LABELS[status] || status}
    </Badge>
  );
}

export default function Children() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canCreate = hasPermission("children:create");
  const canHistoric = hasPermission("children:historic");
  const emptyHistoric = {
    first_name: "", surname: "", date_of_birth: "", council_id: "", school_id: "",
    historic_training_period: "", historic_trainer_name: "", historic_summary: "",
  };
  const [histOpen, setHistOpen] = useState(false);
  const [histSaving, setHistSaving] = useState(false);
  const [hist, setHist] = useState(emptyHistoric);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [councilFilter, setCouncilFilter] = useState("all");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [trainerFilter, setTrainerFilter] = useState("all");
  const [councils, setCouncils] = useState([]);
  const [schools, setSchools] = useState([]);
  const [trainers, setTrainers] = useState([]);

  const isAdmin = hasPermission("children:read_all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "all") params.case_status = statusFilter;
      if (councilFilter !== "all") params.council_id = councilFilter;
      if (schoolFilter !== "all") params.school_id = schoolFilter;
      if (trainerFilter !== "all") params.trainer_id = trainerFilter;
      const { data } = await api.get("/children", { params });
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, councilFilter, schoolFilter, trainerFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/councils").then((r) => setCouncils(r.data.items)).catch(() => {});
      api.get("/schools").then((r) => setSchools(r.data.items)).catch(() => {});
      api.get("/staff", { params: { role: "travel_trainer" } }).then((r) => setTrainers(r.data.items)).catch(() => {});
    }
  }, [isAdmin]);

  const submitHistoric = async () => {
    if (!hist.first_name.trim() || !hist.surname.trim()) {
      toast.error("First name and surname are required");
      return;
    }
    setHistSaving(true);
    try {
      const payload = {};
      Object.entries(hist).forEach(([k, v]) => { if (v) payload[k] = v; });
      const { data } = await api.post("/children/historic", payload);
      toast.success(`Historic record ${data.case_id} recorded and archived`);
      setHistOpen(false);
      setHist(emptyHistoric);
      navigate(`/children/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setHistSaving(false);
    }
  };

  const setHistField = (k) => (e) => setHist((h) => ({ ...h, [k]: e.target ? e.target.value : e }));

  return (
    <div data-testid="children-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Children</h1>
          <p className="text-sm text-slate-600 mt-1">
            {isAdmin ? "All cases across commissioning local authorities." : "Cases currently allocated to you."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canHistoric && (
            <Button variant="outline" data-testid="historic-record-button" onClick={() => setHistOpen(true)}
              className="h-11 font-semibold">
              <Archive className="h-4 w-4 mr-2" /> Add historic record
            </Button>
          )}
          {canCreate && (
            <Button data-testid="child-add-button" onClick={() => navigate("/children/new")}
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              <Plus className="h-4 w-4 mr-2" /> New referral
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="relative lg:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="child-search-input" placeholder="Search name, case ID or council reference…"
            value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="child-status-filter" className="h-11"><SelectValue placeholder="Case status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" data-testid="filter-status-all">All statuses</SelectItem>
            {Object.entries(CASE_STATUS_LABELS).map(([k, l]) => (
              <SelectItem key={k} value={k} data-testid={`filter-status-${k}`}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin && (
          <>
            <Select value={councilFilter} onValueChange={setCouncilFilter}>
              <SelectTrigger data-testid="child-council-filter" className="h-11"><SelectValue placeholder="Local authority" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all" data-testid="filter-council-all">All local authorities</SelectItem>
                {councils.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={trainerFilter} onValueChange={setTrainerFilter}>
              <SelectTrigger data-testid="child-trainer-filter" className="h-11"><SelectValue placeholder="Trainer" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all" data-testid="filter-trainer-all">All trainers</SelectItem>
                {trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-600" data-testid="children-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading children…
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center" data-testid="children-empty">
            <p className="text-sm text-slate-600">
              {isAdmin ? "No children found. Create the first referral to begin a case." : "No cases are currently allocated to you."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Child</TableHead>
                <TableHead>Case ID</TableHead>
                <TableHead className="hidden md:table-cell">Age</TableHead>
                <TableHead className="hidden lg:table-cell">School</TableHead>
                <TableHead className="hidden lg:table-cell">Local Authority</TableHead>
                <TableHead className="hidden md:table-cell">Trainer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden lg:table-cell">Referral date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`child-row-${c.case_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <StaffAvatar photoId={c.photo_document_id} name={`${c.first_name} ${c.surname}`} testid={`child-avatar-${c.case_id}`} />
                      <Link to={`/children/${c.id}`} data-testid={`child-view-link-${c.case_id}`}
                        className="font-medium text-primary hover:underline">
                        {c.first_name} {c.surname}
                        {c.preferred_name ? <span className="text-xs text-slate-500 block">“{c.preferred_name}”</span> : null}
                      </Link>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{c.case_id}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-slate-600">{c.age ?? "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-600">{c.school_name || "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-600">{c.council_name || "—"}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-slate-600">{c.trainer_name || <span className="text-amber-700">Unassigned</span>}</TableCell>
                  <TableCell><CaseStatusBadge status={c.case_status} testid={`child-status-${c.case_id}`} /></TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-600">
                    {c.referral_date ? format(new Date(c.referral_date), "dd MMM yyyy") : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="historic-record-dialog">
          <DialogHeader>
            <DialogTitle>Add historic record</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            For children whose travel training finished before this system was in use and whose records are held on paper.
            The case is recorded as completed and archived immediately — no referral, assessment or programme workflow applies.
            Scanned paper records can be attached afterwards from the Documents tab.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="hist-first-name">First name *</Label>
              <Input id="hist-first-name" data-testid="historic-first-name" value={hist.first_name} onChange={setHistField("first_name")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hist-surname">Surname *</Label>
              <Input id="hist-surname" data-testid="historic-surname" value={hist.surname} onChange={setHistField("surname")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hist-dob">Date of birth</Label>
              <Input id="hist-dob" type="date" data-testid="historic-dob" value={hist.date_of_birth} onChange={setHistField("date_of_birth")} />
            </div>
            <div className="space-y-1.5">
              <Label>Approximate training period</Label>
              <Input placeholder="e.g. Spring term 2019" data-testid="historic-training-period" value={hist.historic_training_period} onChange={setHistField("historic_training_period")} />
            </div>
            <div className="space-y-1.5">
              <Label>School</Label>
              <Select value={hist.school_id} onValueChange={setHistField("school_id")}>
                <SelectTrigger data-testid="historic-school"><SelectValue placeholder="Select school" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {schools.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`historic-school-${s.id}`}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Local authority</Label>
              <Select value={hist.council_id} onValueChange={setHistField("council_id")}>
                <SelectTrigger data-testid="historic-council"><SelectValue placeholder="Select local authority" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {councils.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`historic-council-${c.id}`}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Trainer name (optional)</Label>
              <Input placeholder="Name recorded on the paper file" data-testid="historic-trainer-name" value={hist.historic_trainer_name} onChange={setHistField("historic_trainer_name")} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Summary of paper records</Label>
              <Textarea rows={4} placeholder="What the paper file contains, outcome, any notes worth keeping…"
                data-testid="historic-summary" value={hist.historic_summary} onChange={setHistField("historic_summary")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistOpen(false)} data-testid="historic-cancel-button">Cancel</Button>
            <Button onClick={submitHistoric} disabled={histSaving} data-testid="historic-save-button"
              className="bg-primary hover:bg-brand-hover text-white font-semibold">
              {histSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record & archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}